import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE } from '../services/api';

const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n || 0);
const fmtFecha = (f) => f ? new Date(f).toLocaleDateString('es-CO') : '—';

export default function PropuestaPublica() {
  const { token } = useParams();
  const [cot,      setCot]      = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [modal,    setModal]    = useState(null); // 'aprobar' | 'rechazar'
  const [nombre,   setNombre]   = useState('');
  const [motivo,   setMotivo]   = useState('');
  const [saving,   setSaving]   = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/propuesta/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.error);
        else setCot(d);
      })
      .catch(() => setError('No se pudo cargar la propuesta'))
      .finally(() => setLoading(false));
  }, [token]);

  const responder = async (accion) => {
    if (!nombre.trim()) return alert('Por favor ingrese su nombre para firmar');
    if (accion === 'rechazar' && !motivo.trim()) return alert('Por favor indique el motivo del rechazo');
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/propuesta/${token}/responder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accion, nombre_firmante: nombre, motivo }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Error al procesar');
      setResultado({ accion, estado: data.estado });
      setModal(null);
      setCot(prev => ({ ...prev, estado: data.estado }));
    } catch (e) {
      alert(e.message);
    } finally { setSaving(false); }
  };

  const [descargando, setDescargando] = useState(false);
  const descargarPDF = async () => {
    if (descargando) return;
    setDescargando(true);
    try {
      const r = await fetch(`${API_BASE}/api/propuesta/${token}/pdf`);
      if (!r.ok) throw new Error('No se pudo generar el PDF');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${cot?.numero || 'cotizacion'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message || 'Error al descargar'); }
    finally { setDescargando(false); }
  };

  if (loading) return (
    <div style={s.page}>
      <div style={s.loader}>Cargando propuesta...</div>
    </div>
  );

  if (error) return (
    <div style={s.page}>
      <div style={s.errorBox}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
        <h2 style={{ color: '#1e293b', marginBottom: 8 }}>Propuesta no encontrada</h2>
        <p style={{ color: '#64748b' }}>{error}</p>
      </div>
    </div>
  );

  const items = cot.items || [];
  const yaRespondida = ['aprobada','rechazada','facturada'].includes(cot.estado);

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerInner}>
          <div style={s.logo}>ERP INGENIERÍA</div>
          <div style={s.docInfo}>
            <div style={s.docNum}>{cot.numero}</div>
            <div style={s.docSub}>Propuesta Comercial</div>
          </div>
        </div>
      </div>

      <div style={s.body}>
        {/* Resultado / estado */}
        {resultado && (
          <div style={{ ...s.alertBox, background: resultado.accion === 'aprobar' ? '#dcfce7' : '#fee2e2', borderColor: resultado.accion === 'aprobar' ? '#86efac' : '#fca5a5' }}>
            <span style={{ fontSize: 24 }}>{resultado.accion === 'aprobar' ? '✅' : '❌'}</span>
            <div>
              <strong style={{ color: resultado.accion === 'aprobar' ? '#166534' : '#991b1b' }}>
                {resultado.accion === 'aprobar' ? 'Propuesta aprobada exitosamente' : 'Propuesta rechazada'}
              </strong>
              <p style={{ margin: '4px 0 0', color: '#475569', fontSize: 13 }}>
                Hemos notificado al equipo. Nos pondremos en contacto pronto.
              </p>
            </div>
          </div>
        )}

        {/* Estado actual */}
        {!resultado && yaRespondida && (
          <div style={{ ...s.alertBox, background: cot.estado === 'aprobada' ? '#dcfce7' : '#fee2e2', borderColor: cot.estado === 'aprobada' ? '#86efac' : '#fca5a5' }}>
            <span style={{ fontSize: 24 }}>{cot.estado === 'aprobada' ? '✅' : '❌'}</span>
            <div>
              <strong style={{ color: cot.estado === 'aprobada' ? '#166534' : '#991b1b' }}>
                Esta propuesta ya fue {cot.estado}
              </strong>
              {cot.aprobado_por && <p style={{ margin: '4px 0 0', color: '#475569', fontSize: 13 }}>Por: {cot.aprobado_por} · {fmtFecha(cot.aprobado_en)}</p>}
              {cot.rechazado_por && <p style={{ margin: '4px 0 0', color: '#475569', fontSize: 13 }}>Por: {cot.rechazado_por} · {fmtFecha(cot.rechazado_en)}</p>}
            </div>
          </div>
        )}

        {/* Cliente */}
        <div style={s.section}>
          <div style={s.sectionTitle}>DESTINATARIO</div>
          <div style={s.infoRow}><span style={s.infoLabel}>Empresa</span><strong>{cot.cliente_nombre}</strong></div>
          <div style={s.infoRow}><span style={s.infoLabel}>NIT</span><span>{cot.nit}</span></div>
          {cot.cliente_ciudad && <div style={s.infoRow}><span style={s.infoLabel}>Ciudad</span><span>{cot.cliente_ciudad}</span></div>}
        </div>

        {/* Fechas */}
        <div style={s.fechasGrid}>
          <div style={s.fechaBox}>
            <div style={s.fechaLabel}>Fecha de emisión</div>
            <div style={s.fechaVal}>{fmtFecha(cot.fecha_emision)}</div>
          </div>
          <div style={s.fechaBox}>
            <div style={s.fechaLabel}>Válida hasta</div>
            <div style={s.fechaVal}>{fmtFecha(cot.fecha_vencimiento)}</div>
          </div>
          <div style={s.fechaBox}>
            <div style={s.fechaLabel}>Estado</div>
            <div style={{ ...s.fechaVal, color: cot.estado === 'aprobada' ? '#166534' : cot.estado === 'rechazada' ? '#991b1b' : '#1e3a8a', textTransform: 'capitalize' }}>{cot.estado}</div>
          </div>
        </div>

        {/* Ítems */}
        <div style={s.section}>
          <div style={s.sectionTitle}>DETALLE DE SERVICIOS / PRODUCTOS</div>
          <table style={s.table}>
            <thead>
              <tr style={{ background: '#1e3a8a' }}>
                {['#','Descripción','Cant.','Unid.','Precio unit.','Dto%','IVA','Total'].map(h =>
                  <th key={h} style={s.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={s.td}>{i + 1}</td>
                  <td style={s.td}>{it.descripcion}</td>
                  <td style={{ ...s.td, textAlign: 'center' }}>{it.cantidad}</td>
                  <td style={s.td}>{it.unidad || 'und'}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmt(it.precio_unitario)}</td>
                  <td style={{ ...s.td, textAlign: 'center', color: Number(it.descuento_pct) > 0 ? '#f59e0b' : '#94a3b8' }}>
                    {Number(it.descuento_pct) > 0 ? `${it.descuento_pct}%` : '—'}
                  </td>
                  <td style={{ ...s.td, textAlign: 'center', color: '#64748b' }}>{Number(it.iva_pct ?? 19)}%</td>
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{fmt(it.total_item)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totales */}
        <div style={s.totalesWrap}>
          <div style={s.totalesBox}>
            <div style={s.totalRow}><span>Subtotal</span><span>{fmt(cot.subtotal)}</span></div>
            <div style={s.totalRow}><span>IVA</span><span>{fmt(cot.iva)}</span></div>
            {Number(cot.impoconsumo) > 0 && <div style={s.totalRow}><span>INC</span><span>{fmt(cot.impoconsumo)}</span></div>}
            <div style={s.totalFinal}><span>TOTAL</span><span>{fmt(cot.total)}</span></div>
          </div>
        </div>

        {/* Notas */}
        {cot.notas && (
          <div style={s.notasBox}>
            <strong>Condiciones y observaciones:</strong>
            <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>{cot.notas}</p>
          </div>
        )}

        {/* Descargar PDF — siempre disponible */}
        <div style={{ textAlign: 'center', margin: '24px 0 4px' }}>
          <button type="button" onClick={descargarPDF} disabled={descargando} style={{ ...s.btnPdf, opacity: descargando ? 0.7 : 1, cursor: descargando ? 'wait' : 'pointer' }}>
            {descargando ? '⏳ Generando PDF…' : '📄 Descargar cotización en PDF'}
          </button>
        </div>

        {/* Botones de acción */}
        {!yaRespondida && !resultado && (
          <div style={s.accionesBox}>
            <p style={s.accionesTitle}>¿Desea proceder con esta propuesta?</p>
            <div style={s.btns}>
              <button style={s.btnRechazar} onClick={() => setModal('rechazar')}>
                ✕ Rechazar propuesta
              </button>
              <button style={s.btnAprobar} onClick={() => setModal('aprobar')}>
                ✓ Aprobar propuesta
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={s.footer}>
        Documento generado por ERP Ingeniería · {new Date().getFullYear()}
      </div>

      {/* Modal aprobar/rechazar */}
      {modal && (
        <div style={s.overlay}>
          <div style={s.modalBox}>
            <h2 style={{ color: modal === 'aprobar' ? '#166534' : '#991b1b', marginTop: 0 }}>
              {modal === 'aprobar' ? '✅ Aprobar propuesta' : '❌ Rechazar propuesta'}
            </h2>
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
              {modal === 'aprobar'
                ? 'Al aprobar esta propuesta autorizamos iniciar los trabajos descritos en el documento.'
                : 'Por favor indique el motivo del rechazo para nuestros registros.'}
            </p>
            <label style={s.label}>Su nombre completo *</label>
            <input style={s.input} value={nombre} onChange={e => setNombre(e.target.value)}
              placeholder="Nombre y apellido de quien aprueba/rechaza" />
            {modal === 'rechazar' && (
              <>
                <label style={{ ...s.label, marginTop: 12 }}>Motivo del rechazo *</label>
                <textarea style={{ ...s.input, minHeight: 80, resize: 'vertical' }}
                  value={motivo} onChange={e => setMotivo(e.target.value)}
                  placeholder="Indique el motivo..." />
              </>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
              <button style={s.btnCancelar} onClick={() => setModal(null)}>Cancelar</button>
              <button
                style={{ ...(modal === 'aprobar' ? s.btnAprobar : s.btnRechazar), opacity: saving ? 0.7 : 1 }}
                onClick={() => responder(modal)} disabled={saving}>
                {saving ? 'Procesando...' : modal === 'aprobar' ? 'Confirmar aprobación' : 'Confirmar rechazo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page:        { minHeight: '100vh', background: '#f1f5f9', fontFamily: 'Arial, sans-serif', color: '#1e293b' },
  loader:      { textAlign: 'center', padding: '80px 20px', color: '#64748b', fontSize: 16 },
  errorBox:    { maxWidth: 480, margin: '80px auto', background: '#fff', borderRadius: 12, padding: 40, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,.08)' },
  header:      { background: '#1e3a8a', color: '#fff', padding: '0' },
  headerInner: { maxWidth: 860, margin: '0 auto', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  logo:        { fontSize: 18, fontWeight: 700, letterSpacing: 2 },
  docInfo:     { textAlign: 'right' },
  docNum:      { fontSize: 20, fontWeight: 700 },
  docSub:      { fontSize: 12, opacity: 0.7, marginTop: 2 },
  body:        { maxWidth: 860, margin: '0 auto', padding: '24px 16px' },
  alertBox:    { display: 'flex', gap: 16, alignItems: 'flex-start', border: '1px solid', borderRadius: 10, padding: '16px 20px', marginBottom: 20 },
  section:     { background: '#fff', borderRadius: 10, padding: '20px 24px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
  sectionTitle:{ fontSize: 10, fontWeight: 700, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 },
  infoRow:     { display: 'flex', gap: 12, marginBottom: 6, fontSize: 14 },
  infoLabel:   { color: '#94a3b8', minWidth: 100 },
  fechasGrid:  { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 },
  fechaBox:    { background: '#fff', borderRadius: 10, padding: '14px 18px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
  fechaLabel:  { fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .8, marginBottom: 6 },
  fechaVal:    { fontSize: 15, fontWeight: 700, color: '#1e293b' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:          { padding: '10px 12px', color: '#fff', textAlign: 'left', fontSize: 11, fontWeight: 600 },
  td:          { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13 },
  totalesWrap: { display: 'flex', justifyContent: 'flex-end', marginBottom: 16 },
  totalesBox:  { background: '#fff', borderRadius: 10, padding: '16px 24px', minWidth: 280, boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
  totalRow:    { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#64748b', marginBottom: 6 },
  totalFinal:  { display: 'flex', justifyContent: 'space-between', fontSize: 17, fontWeight: 700, color: '#1e3a8a', borderTop: '2px solid #e2e8f0', paddingTop: 10, marginTop: 6 },
  notasBox:    { background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '16px 20px', marginBottom: 16, fontSize: 13, color: '#78350f' },
  accionesBox: { background: '#fff', borderRadius: 12, padding: '28px 24px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,.08)', border: '1px solid #e2e8f0' },
  accionesTitle:{ color: '#334155', fontSize: 15, fontWeight: 600, marginBottom: 20, marginTop: 0 },
  btns:        { display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' },
  btnAprobar:  { background: '#166534', color: '#fff', border: 'none', borderRadius: 10, padding: '14px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  btnRechazar: { background: '#fff', color: '#991b1b', border: '2px solid #fca5a5', borderRadius: 10, padding: '14px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  btnPdf:      { display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', color: '#1e3a8a', border: '1px solid #c7d2fe', borderRadius: 10, padding: '10px 22px', fontSize: 14, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' },
  btnCancelar: { background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer' },
  footer:      { textAlign: 'center', color: '#94a3b8', fontSize: 11, padding: '20px', borderTop: '1px solid #e2e8f0', marginTop: 20 },
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modalBox:    { background: '#fff', borderRadius: 16, padding: '28px 32px', width: '90%', maxWidth: 480, boxShadow: '0 8px 32px rgba(0,0,0,.2)' },
  label:       { display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6 },
  input:       { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#1e293b', boxSizing: 'border-box', outline: 'none' },
};
